import { DisconnectOutlined, LinkOutlined, LoadingOutlined } from '@ant-design/icons';
import { notification } from 'antd';
import dayjs from 'dayjs';
import 'dayjs/locale/vi';
import 'dayjs/locale/en'; // Import Vietnamese locale if needed
import relativeTime from 'dayjs/plugin/relativeTime';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { socket } from './Socket';
import parse from 'html-react-parser';
import { NotificationContext } from 'context/NotificationContext';
import { createNotification } from 'utils/noti';
dayjs.extend(relativeTime);
dayjs.locale(localStorage.getItem('i18nextLng'));

var customParseFormat = require('dayjs/plugin/customParseFormat');

dayjs.extend(customParseFormat);

function generateRandomId(length = 10) {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let randomId = '';
    for (let i = 0; i < length; i++) {
        const randomIndex = Math.floor(Math.random() * characters.length);
        randomId += characters[randomIndex];
    }
    return randomId;
}

export const triggerNotification = (message = {}) => {
    try {
        const item = message.message ?? {};
        const status = item.status || 'info';
        const createdTime = item.create_date ? dayjs(item.create_date, 'DD/MM/YYYY HH:mm:ss') : dayjs();
        const timeAgo = item.create_date ? createdTime.fromNow() : '';

        notification[status]({
            className: message.navigate && item.navigate ? 'pointer-cursor' : '',
            ...item,
            message: parse(item.message),
            description: timeAgo,
            onClick: () => {
                if (message.navigate && item.navigate) {
                    message.navigate(item.navigate);
                    closeNotification(item.id);
                }
            },
        });
    } catch (err) {
        notification.error('Đã xảy ra lỗi' + err);
    }
};


export const closeNotification = (id) => {
    notification.destroy(id);
};

export const pingServer = async () => {};

const GeneralNotification = () => {
    const { t } = useTranslation(['common', 'fob_notification']);
    const [online, setOnline] = useState(true);
    const [onlineStatus, setOnlineStatus] = useState('');
    const navigate = useNavigate();
    const context = useContext(NotificationContext);
    const { setConsumerNotification, unreadConsumerNotification, setUnreadConsumerNotification } = context;

    useEffect(() => {
        // console.log('change here');
        const handleConnect = () => {
            setOnline(true);
            closeNotification('client_offline');
            closeNotification('server_offline');
            if (onlineStatus) {
                if (onlineStatus === 'no_internet') {
                    handleClientOnline();
                } else {
                    handleServerOnline();
                }
            }
        };

        const handleDisconnect = () => {
            setOnline(false);
            if (!navigator.onLine) {
                setOnlineStatus('no_internet');
                handleClientOffline();
            } else {
                setOnlineStatus('server');
                handleServerOffline();
            }
        };

        socket.on('connect', handleConnect);

        socket.on('reconnect', handleConnect);

        socket.on('connect_error', handleDisconnect);

        socket.on('noti', (item) => {
            const jsonItem = JSON.parse(item);
            const notification = createNotification({
                id: jsonItem.id,
                message: jsonItem.message,
                navigate: jsonItem.navigate,
                placement: jsonItem.placement,
                status: jsonItem.status,
                create_date: jsonItem.create_date,
                read: jsonItem.status === 'read' ? true : false
            });
            setConsumerNotification((prev) => [notification, ...prev]);
            if (!notification.read) setUnreadConsumerNotification(prv => prv + 1);
            triggerNotification({ message: notification, navigate: navigate });
            
        });

        checkConnection();
        if ('connection' in navigator) {
            const connection = navigator.connection;
            connection.addEventListener('change', checkConnection);
        }

        return () => {
            if ('connection' in navigator) {
                const connection = navigator.connection;
                connection.removeEventListener('change', checkConnection);
            }
        };
    }, []);

    const handleServerOnline = () => {
        closeNotification('server_offline');
        const serverOnlineNotification = createNotification({
            message: t('fob_notification:server_online'),
            icon: <LinkOutlined style={{ color: '#32a852' }} />,
            key: 'server_online',
        });
        triggerNotification({ message: serverOnlineNotification });
    };

    const handleServerOffline = () => {
        closeNotification('server_online');
        const serverOfflineNotification = createNotification({
            message: t('fob_notification:server_offline'),
            icon: <DisconnectOutlined style={{ color: '#dc3545' }} />,
            key: 'server_offline',
            duration: 0, // Set to 0 for indefinite display
        });
        triggerNotification({ message: serverOfflineNotification });
    };

    const handleClientOnline = () => {
        closeNotification('client_offline');
        const clientOnlineNotification = createNotification({
            message: t('fob_notification:client_online'),
            icon: <LinkOutlined style={{ color: '#32a852' }} />,
            key: 'client_online',
        });
        triggerNotification({ message: clientOnlineNotification }, setConsumerNotification);
    };

    const handleClientOffline = () => {
        closeNotification('client_online');
        const clientOfflineNotification = createNotification({
            message: t('fob_notification:client_offline'),
            icon: <DisconnectOutlined style={{ color: '#dc3545' }} />,
            key: 'client_offline',
            duration: 0, // Set to 0 for indefinite display
        });
        triggerNotification({ message: clientOfflineNotification });
    };

    // Dùng Navigator Connection API để kiểm tra tốc độ mạng
    const checkConnection = () => {
        if ('connection' in navigator) {
            const connection = navigator.connection;

            if (connection.effectiveType === '2g') {
                const speed = connection.downlink;
                const slowConnectionNotification = createNotification({
                    message: `${t('fob_notification:slow_connection')} (${speed} Mbps)`,
                    icon: <LoadingOutlined />,
                    key: 'slow-connection',
                    duration: 0, // Set to 0 for indefinite display,
                });
                triggerNotification({ message: slowConnectionNotification });
                if (!online) {
                    handleClientOnline();
                    setOnline(true);
                    setOnlineStatus('');
                }
            } else {
                closeNotification('slow-connection');
            }
        }
    };

    return null;
};

export default GeneralNotification;
